import { useState } from "react";
import {
  Button,
  Label,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@tonic/shared/components/ui';
import { useLocations } from "../../hooks/useLocations";
import { useCreateTransfer } from "../../hooks/useTransfers";
import { TransferableItemsTable } from "./TransferableItemsTable";
import PropTypes from "prop-types";

/**
 * @typedef {Object} CreateTransferDialogProps
 * @property {boolean} open - Whether the dialog is open
 * @property {(open: boolean) => void} onOpenChange - Callback when dialog open state changes
 */

/**
 * Dialog component for creating new inventory transfers
 * @param {CreateTransferDialogProps} props
 * @returns {JSX.Element} The create transfer dialog component
 */
export function CreateTransferDialog({ open, onOpenChange }) {
  const createTransfer = useCreateTransfer();
  const { data: locations } = useLocations();
  const [formData, setFormData] = useState({
    sourceLocationId: "",
    destinationLocationId: "",
    items: [],
  });

  /**
   * Handles form submission to create a new transfer
   * @param {React.FormEvent} e - Form event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createTransfer.mutateAsync({
        sourceLocationId: formData.sourceLocationId,
        destinationLocationId: formData.destinationLocationId,
        items: formData.items,
      });
      onOpenChange(false);
      setFormData({
        sourceLocationId: "",
        destinationLocationId: "",
        items: [],
      });
    } catch (error) {
      console.error("Failed to create transfer:", error);
    }
  };

  const availableDestinations = locations?.filter(
    (location) => location.id !== formData.sourceLocationId
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create Transfer</DialogTitle>
            <DialogDescription>
              Transfer inventory items between locations.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Location</Label>
                <Select
                  value={formData.sourceLocationId}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      sourceLocationId: value,
                      items: [], // Reset items when source changes
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select source location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations?.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>To Location</Label>
                <Select
                  value={formData.destinationLocationId}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      destinationLocationId: value,
                    }))
                  }
                  disabled={!formData.sourceLocationId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select destination location" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDestinations?.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.sourceLocationId && (
              <div className="space-y-2">
                <Label>Select Items to Transfer</Label>
                <TransferableItemsTable
                  locationId={formData.sourceLocationId}
                  selectedItems={formData.items}
                  onItemsChange={(items) =>
                    setFormData((prev) => ({ ...prev, items }))
                  }
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                createTransfer.isLoading ||
                !formData.sourceLocationId ||
                !formData.destinationLocationId ||
                formData.items.length === 0
              }
            >
              {createTransfer.isLoading ? "Creating..." : "Create Transfer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

CreateTransferDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
}; 
